import { type ChangeEvent } from "react";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { useFormikContext, getIn } from "formik";
import { type Moment } from "moment-timezone";

import { type IncidentReportRiskAssessmentCore, type IncidentReportNotifiedPartyType } from "components/CriticalIncident/interfaces";
import { type MultiSelectOption } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import {
  NOTIFIED_PARTY_LABEL_KEYS,
  NOTIFIED_PARTY_TYPES,
  RISK_FACTOR_LEVEL_OPTIONS,
  RISK_FACTOR_OPTIONS,
  makeEmptyNotifiedParty,
} from "../RiskAssessments.constants";

// Field-level actions for a single risk-assessment record, addressed by `basePath` (a Formik
// field path, e.g. "riskAssessment.participants[2]" for the standalone non-AU section, or
// "individualSupportSession.participants[1].riskAssessment" for the AU-embedded block). This
// is what lets RiskFactorsSection/ActionsTakenSection/NotifiedPartyFields be shared by both.
export function useRiskAssessmentFieldActions(
  basePath: string,
  record: IncidentReportRiskAssessmentCore,
  patchRecord: (partial: Partial<IncidentReportRiskAssessmentCore>) => void,
  isReadonly: boolean,
) {
  const { t } = useTranslation("criticalIncident", { useSuspense: false });
  const { touched, errors, setFieldValue } = useFormikContext();
  const recordTouched = getIn(touched, basePath) as Record<string, unknown> | undefined;
  const recordErrors = getIn(errors, basePath) as Record<string, string> | undefined;

  const riskFactorLevelOptions = RISK_FACTOR_LEVEL_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));
  const riskFactorOptions: MultiSelectOption[] = RISK_FACTOR_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));
  const notifiedPartyOptions: MultiSelectOption[] = NOTIFIED_PARTY_TYPES.map((type) => ({
    value: type,
    label: t(NOTIFIED_PARTY_LABEL_KEYS[type]),
  }));

  const updateField = (field: string, value: unknown) => {
    setFieldValue(`${basePath}.${field}`, value);
    patchRecord({ [field]: value } as Partial<IncidentReportRiskAssessmentCore>);
  };

  const makeParticipantChangeHandler = (field: keyof IncidentReportRiskAssessmentCore) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      updateField(field, event.target.value);
    };

  const handleParticipantBooleanChange = (field: keyof IncidentReportRiskAssessmentCore) =>
    (value: boolean | null) => {
      updateField(field, value);
    };

  const handleParticipantValueChange = (field: keyof IncidentReportRiskAssessmentCore) =>
    (value: string) => {
      updateField(field, value);
    };

  const handleParticipantRiskFactorLevelChange = (field: keyof IncidentReportRiskAssessmentCore) =>
    (event: SelectChangeEvent<string[]>) => {
      updateField(field, event.target.value as string[]);
    };

  const handleRiskFactorsChange = () => (event: SelectChangeEvent<string[]>) => {
    updateField("riskFactors", event.target.value as string[]);
  };

  const handleNotifiedPartyTypesChange = () => async (event: SelectChangeEvent<string[]>): Promise<boolean> => {
    const rawSelected = event.target.value as IncidentReportNotifiedPartyType[];
    const previousSelected = record.selectedNotifiedParties ?? [];
    const noneJustSelected = rawSelected.includes("none") && !previousSelected.includes("none");
    const otherJustSelected = rawSelected.some((type) => type !== "none") && previousSelected.includes("none");

    const nextSelected: IncidentReportNotifiedPartyType[] = noneJustSelected
      ? ["none"]
      : otherJustSelected
        ? rawSelected.filter((type) => type !== "none")
        : rawSelected;

    const removedTypes = previousSelected.filter((type) => !nextSelected.includes(type));
    const isClearingNone = previousSelected.includes("none");
    let removalConfirmed = false;

    if (removedTypes.length && !isClearingNone) {
      const removedLabels = removedTypes.map((type) => t(NOTIFIED_PARTY_LABEL_KEYS[type])).join(", ");
      const confirmed = await confirm({
        message: t("remove_notified_parties_confirm_text", { label: removedLabels }),
        confirmLabel: t("yes_remove"),
      });
      if (!confirmed) return false;
      removalConfirmed = true;
    }

    const existingParties = record.partiesNotified ?? [];
    const nextPartiesNotified = nextSelected
      .filter((type) => type !== "none")
      .map((type) => existingParties.find((party) => party.partyType === type) ?? makeEmptyNotifiedParty(type));

    setFieldValue(`${basePath}.selectedNotifiedParties`, nextSelected);
    setFieldValue(`${basePath}.partiesNotified`, nextPartiesNotified);
    patchRecord({ selectedNotifiedParties: nextSelected, partiesNotified: nextPartiesNotified });

    return removalConfirmed;
  };

  const updateNotifiedPartyField = (partyIndex: number, field: string, value: unknown) => {
    const nextPartiesNotified = (record.partiesNotified ?? []).map((party, i) =>
      i === partyIndex ? { ...party, [field]: value } : party
    );
    updateField("partiesNotified", nextPartiesNotified);
  };

  const makeNotifiedPartyChangeHandler = (partyIndex: number, field: string) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      updateNotifiedPartyField(partyIndex, field, event.target.value);
    };

  const handleNotifiedPartyDateChange = (partyIndex: number) => (value: Moment | null) => {
    const isoString = value?.toDate().toISOString() || "";
    updateNotifiedPartyField(partyIndex, "notifiedDate", isoString);
  };

  const handleNotifiedPartyBooleanChange = (partyIndex: number, field: string) =>
    (value: boolean | null) => {
      updateNotifiedPartyField(partyIndex, field, value);
    };

  return {
    basePath,
    record,
    recordTouched,
    recordErrors,
    isReadonly,
    riskFactorLevelOptions,
    riskFactorOptions,
    notifiedPartyOptions,
    makeParticipantChangeHandler,
    handleParticipantBooleanChange,
    handleParticipantValueChange,
    handleParticipantRiskFactorLevelChange,
    handleRiskFactorsChange,
    handleNotifiedPartyTypesChange,
    makeNotifiedPartyChangeHandler,
    handleNotifiedPartyDateChange,
    handleNotifiedPartyBooleanChange,
  };
}
